import React, {
  ChangeEvent,
  KeyboardEventHandler,
  ReactNode,
  useState,
} from 'react';

import {
  calculateSuggestedDrawAmounts,
  currencyString,
  currencyStringWithoutSymbol,
  parseCurrencyAmount,
  sanitizeCurrencyInput,
} from '@consumer-frontend/nc-utils';

import { NcButton } from '../NcButton';
import { NcCardPanel } from '../NcCard';
import { NcInput } from '../NcInput';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/draw-request/requested-draw-amount-input.scss';

export type NcRequestedDrawAmountInputPropsT = {
  maxDrawAmount: number;
  minDrawAmount: number;
  defaultValue?: number;
  errorMessage?: ReactNode;
  onBlur?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  ref?: React.Ref<HTMLInputElement>;
  updateValue?: (value?: number) => void;
};
export type NcRequestedDrawAmountInputT = React.FC<NcRequestedDrawAmountInputPropsT>;

export const NcRequestedDrawAmountInput: NcRequestedDrawAmountInputT = ({
  errorMessage,
  defaultValue,
  maxDrawAmount,
  minDrawAmount,
  onBlur,
  onKeyDown,
  ref,
  updateValue,
}) => {
  const suggestedAmounts = calculateSuggestedDrawAmounts({ maxAmount: maxDrawAmount, minAmount: minDrawAmount });

  const [displayValue, setDisplayValue] = useState<string>(() => currencyStringWithoutSymbol(defaultValue ?? maxDrawAmount));

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const sanitizedValue = sanitizeCurrencyInput(event.target.value);

    setDisplayValue(sanitizedValue);
  };

  // Autofill zero cents and group thousands on blur; the field stays freely editable while focused.
  const handleInputBlur = (): void => {
    const amount = parseCurrencyAmount(displayValue);

    setDisplayValue(amount === undefined ? '' : currencyStringWithoutSymbol(amount));

    updateValue?.(amount);
    onBlur?.();
  };

  const handleSuggestionClick = (amount: number): void => {
    setDisplayValue(currencyStringWithoutSymbol(amount));
    updateValue?.(amount);
  };

  return (
    <NcCardPanel className="nc-requested-draw-amount-input">
      <NcInput
        className="nc-requested-draw-amount-input-field"
        errorMessage={errorMessage}
        helperText="Enter or select an amount."
        id="requested-draw-amount"
        inputMode="decimal"
        label="Requested Amount"
        name="requested-draw-amount"
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
        prefix="$"
        ref={ref}
        type="text"
        value={displayValue}
      />

      {!!suggestedAmounts.length && (
        <div className="nc-requested-draw-amount-input-suggestions">
          {suggestedAmounts.map((amount) => (
            <NcButton
              key={amount}
              className="nc-requested-draw-amount-input-suggestion"
              onClick={() => handleSuggestionClick(amount)}
              size="sm"
              type="button"
              variant="outline-primary"
            >
              {currencyString(amount, true)}
            </NcButton>
          ))}
        </div>
      )}

      <dl className="min-max-values-list">
        <div>
          <dt>Minimum</dt>
          <dd>{currencyString(minDrawAmount)}</dd>
        </div>
        <div>
          <dt>Maximum</dt>
          <dd>{currencyString(maxDrawAmount)}</dd>
        </div>
      </dl>
    </NcCardPanel>
  );
};
